import { useEffect, useRef } from "react";
import { TrendingUp, Users, ShoppingBag, BarChart2 } from "lucide-react";

// ─── Mini Donut Chart ─────────────────────────────────────────────────────────
function MultiRingDonut({ data, size = 220 }) {
const ref = useRef(null);

useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    canvas.width = size;
    canvas.height = size;

    const cx = size / 2;
    const cy = size / 2;

    ctx.clearRect(0, 0, size, size);

    const ringWidth = 12;
    const gap = 8;

    data.forEach((item, index) => {
    const radius = size / 2 - index * (ringWidth + gap) - 10;

    // background circle (light gray)
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.lineWidth = ringWidth;
    ctx.stroke();

    // value arc
    const start = -Math.PI / 2;
    const end = start + (item.value / 100) * 2 * Math.PI;

    ctx.beginPath();
    ctx.arc(cx, cy, radius, start, end);
    ctx.strokeStyle = item.color;
    ctx.lineWidth = ringWidth;
    ctx.lineCap = "round";
    ctx.stroke();
    });

}, [data, size]);

return <canvas ref={ref} />;
}

// ─── Stat Row ─────────────────────────────────────────────────────────────────
function StatRow({ icon: Icon, iconColor, iconBg, label, value, change }) {
return (
    <div
    style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px",
        borderRadius: "10px",
        background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.12)",
        backdropFilter: "blur(6px)",
    }}
    >
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div
        style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            background: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
        }}
        >
        <Icon size={14} color={iconColor} />
        </div>
        <div>
        <p style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", margin: 0, lineHeight: 1 }}>
            {label}
        </p>
        <p style={{ fontSize: 15, fontWeight: 700, color: "#fff", margin: "2px 0 0", lineHeight: 1 }}>
            {value}
        </p>
        </div>
    </div>
    <span
        style={{
        fontSize: 10,
        fontWeight: 600,
        color: "#6ee7b7",
        background: "rgba(110,231,183,0.15)",
        padding: "3px 8px",
        borderRadius: 20,
        display: "flex",
        alignItems: "center",
        gap: 3,
        }}
    >
        <TrendingUp size={9} color="#6ee7b7" />
        {change}
    </span>
    </div>
);
}

// ─── Main Card ────────────────────────────────────────────────────────────────
export default function DashboardOverviewCard({
totalOnboarding,
totalProductSales,
customerCount,
onboardingChange,
customerChange,
productChange,
topProductName,
}) {
const total = totalOnboarding + totalProductSales + customerCount;
const pctOf = (v) => {
  if (!total || isNaN(total)) return 0;
  return Math.round((v / total) * 100);
};

const segments = [
    { value: totalOnboarding,   color: "#a5f3fc" },
    { value: customerCount,     color: "#c4b5fd" },
    { value: totalProductSales, color: "#6ee7b7" },
];

const legendItems = [
    { color: "#a5f3fc", label: "Retailer onboarding", pct: pctOf(totalOnboarding) },
    { color: "#c4b5fd", label: "Customer growth",     pct: pctOf(customerCount) },
    { color: "#6ee7b7", label: "Product sales",       pct: pctOf(totalProductSales) },
];

const fmtCount = (n) =>
    n >= 1_000
    ? `${Math.floor(n / 1000)},${String(n % 1000).padStart(3, "0")}`
    : String(n);

return (
<div className="relative flex flex-col h-full py-2 px-4 rounded-2xl overflow-hidden text-white
    bg-gradient-to-br from-blue-900 via-blue-600 to-purple-600 font-sans">

    {/* Decorative blobs */}
    <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/10" />
    <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-white/10" />
    <div className="absolute top-24 right-8 w-14 h-14 rounded-full bg-white/10" />

    {/* Header */}
    <p className="text-[10px] font-semibold tracking-widest text-white/60 uppercase">
    Overview
    </p>
    <h3 className="text-lg font-bold mb-4">Dashboard summary</h3>

    {/* Content */}
    <div className="flex items-center gap-4 mb-4">

    {/* Donut */}
    <div className="relative">
        <MultiRingDonut
            size={240}
            data={[
                { value: pctOf(totalOnboarding), color: "#22d3ee" }, // cyan
                { value: pctOf(customerCount), color: "#a78bfa" },   // purple
                { value: pctOf(totalProductSales), color: "#34d399" } // green
            ]}
            />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-extrabold">
            {total ? pctOf(totalOnboarding + customerCount) : 0}%
        </span>
        <span className="text-[10px] text-white/60">active</span>
        </div>
    </div>

    {/* Stats */}
    <div className="flex flex-col gap-2 w-full">

        {/* Row */}
        <div className="flex items-center justify-between p-2 rounded-lg bg-white/10 border border-white/20 backdrop-blur">
        <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-md bg-cyan-200/20">
            <BarChart2 size={14} className="text-cyan-200" />
            </div>
            <div>
            <p className="text-[10px] text-white/60">Total onboarded</p>
            <p className="text-sm font-bold">{fmtCount(totalOnboarding)}</p>
            </div>
        </div>
        <span className="text-[10px] px-2 py-1 rounded-full bg-green-200/20 text-green-300 flex items-center gap-1">
            <TrendingUp size={10} />
            {onboardingChange}
        </span>
        </div>

        <div className="flex items-center justify-between p-2 rounded-lg bg-white/10 border border-white/20 backdrop-blur">
        <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-md bg-cyan-200/20">
            <Users size={14} className="text-cyan-200" />
            </div>
            <div>
            <p className="text-[10px] text-white/60">Warranty Sold</p>
            <p className="text-sm font-bold">{fmtCount(totalProductSales)}</p>
            </div>
        </div>
        <span className="text-[10px] px-2 py-1 rounded-full bg-green-200/20 text-green-300 flex items-center gap-1">
            <TrendingUp size={10} />
            {customerChange}
        </span>
        </div>

        <div className="flex items-center justify-between p-2 rounded-lg bg-white/10 border border-white/20 backdrop-blur">
        <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-md bg-cyan-200/20">
            <ShoppingBag size={14} className="text-cyan-200" />
            </div>
            <div>
            <p className="text-[10px] text-white/60">Top saled Product</p>
            {/* <p className="text-sm font-bold truncate max-w-[140px]">
                {topProductName}
                </p> */}
            <p className="text-sm font-bold truncate max-w-[140px]">
            {topProductName}
            </p>
            </div>
        </div>
        <span className="text-[10px] px-2 py-1 rounded-full bg-green-200/20 text-green-300 flex items-center gap-1">
            <TrendingUp size={10} />
            {productChange}
        </span>
        </div>
    </div>
    </div>
</div>
);
}
